import React, { useCallback, useEffect, useState } from "react";
import { Link2 } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "../../lib/api";
import { formatWhen } from "./ssoFormat";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "../ui/dialog";

const REASONS = {
  no_match: "No match", ambiguous: "Matches more than one user", not_admin: "Not an administrator",
  inactive: "Inactive", other_org: "Other organisation", linked_elsewhere: "Linked to another Microsoft account",
};

function LinkDialog({ denial, admins, selfId, onClose, onLinked }) {
  const [userId, setUserId] = useState("");
  const [busy, setBusy] = useState(false);
  const choices = admins.filter((a) => a.id !== selfId);
  const submit = async () => {
    setBusy(true);
    try { await api.post(`/admin/sso/denials/${denial.id}/link`, { user_id: userId }); toast.success("Microsoft username linked"); onLinked(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  return (
    <Dialog open={Boolean(denial)} onOpenChange={(open) => { if (!open) { setUserId(""); onClose(); } }}>
      <DialogContent data-testid="denial-link-dialog">
        <DialogHeader>
          <DialogTitle>Link to user</DialogTitle>
          <DialogDescription>
            Adds <span className="font-mono">{denial?.username || denial?.email}</span> as a Microsoft username for the
            administrator you choose. Their next Microsoft 365 sign-in will match.
          </DialogDescription>
        </DialogHeader>
        <select className="input-flat my-4" value={userId} onChange={(e) => setUserId(e.target.value)} data-testid="denial-link-user">
          <option value="">Choose an administrator…</option>
          {choices.map((a) => <option key={a.id} value={a.id}>{a.name} ({a.work_email})</option>)}
        </select>
        <DialogFooter>
          <button className="btn-ghost" onClick={onClose} data-testid="denial-link-cancel">Cancel</button>
          <button className="btn-primary" disabled={!userId || busy} onClick={submit} data-testid="denial-link-confirm">Link</button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function SsoDenialsCard({ selfId }) {
  const [data, setData] = useState(null);
  const [linking, setLinking] = useState(null);
  const load = useCallback(() => {
    api.get("/admin/sso/denials").then(({ data: d }) => setData(d)).catch((e) => toast.error(formatApiError(e.response?.data?.detail)));
  }, []);
  useEffect(() => { load(); }, [load]);
  if (!data) return null;
  return (
    <section className="border p-6" style={{ borderColor: "var(--border)", background: "var(--surface)" }} data-testid="sso-denials-card">
      <div className="eyebrow mb-1">Last 30 days</div>
      <h3 className="font-heading font-bold text-lg mb-4" style={{ color: "var(--ink)" }}>Recent refused Microsoft sign-ins</h3>
      {!data.denials.length && <p className="text-sm" style={{ color: "var(--muted)" }} data-testid="sso-denials-empty">No refused Microsoft sign-ins.</p>}
      {data.denials.length > 0 && (
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="sso-denials-table">
            <thead><tr className="text-left eyebrow"><th className="py-2 pr-4">When</th><th className="pr-4">Username</th><th className="pr-4">Email</th><th className="pr-4">Reason</th><th /></tr></thead>
            <tbody>
              {data.denials.map((d) => (
                <tr key={d.id} className="border-t align-top" style={{ borderColor: "var(--border)" }} data-testid={`sso-denial-${d.id}`}>
                  <td className="py-2 pr-4 whitespace-nowrap">{formatWhen(d.at)}</td>
                  <td className="pr-4 font-mono text-xs py-2">{d.username || "—"}</td>
                  <td className="pr-4 font-mono text-xs py-2">{d.email || "—"}</td>
                  <td className="pr-4 py-2" data-testid={`sso-denial-reason-${d.id}`}>
                    {REASONS[d.reason] || d.reason}
                    {d.matched.length > 0 && <div className="text-xs" style={{ color: "var(--muted)" }}>Matched: {d.matched.map((m) => `${m.name || m.username} (${m.work_email})`).join(", ")}</div>}
                    {d.reason === "ambiguous" && !d.resolved && <div className="text-xs mt-1" style={{ color: "var(--muted)" }} data-testid={`sso-denial-hint-${d.id}`}>Remove this address from all but one of these records on the Users page, then ask the person to sign in again.</div>}
                  </td>
                  <td className="py-2 text-right whitespace-nowrap">
                    {d.resolved
                      ? <span className="text-xs" style={{ color: "var(--muted)" }} data-testid={`sso-denial-resolved-${d.id}`}>Linked to {d.linked_to}</span>
                      : d.linkable && (
                        <button className="btn-ghost !py-1 !px-2 text-xs inline-flex items-center gap-1" onClick={() => setLinking(d)} data-testid={`sso-denial-link-${d.id}`}><Link2 size={12} /> Link to user</button>
                      )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <LinkDialog denial={linking} admins={data.admins} selfId={selfId} onClose={() => setLinking(null)} onLinked={() => { setLinking(null); load(); }} />
    </section>
  );
}
